import React, { FC, ReactNode } from 'react';
import { SettingTermKeys } from '../../shared/settings';
import { Terms } from '../APIClient';
import { Fetcher, useFetcher } from '../utils';
import { RichMarkdownIt } from '.';

interface BaseProps {
  readonly name: SettingTermKeys;
}

interface TermsLoaderProps extends BaseProps {
  readonly value?: Terms;
}

export const TermsLoader: FC<TermsLoaderProps> = ({ name, value }) => {
  const fetch = useFetcher(
    async () => value ?? (await api.retrieveTermsOfAttendance(name)),
    [name, value],
  );

  if (!fetch.data) {
    return <Fetcher result={fetch} />;
  }

  return <RichMarkdownIt source={fetch.data.text} />;
};

interface TermsLoaderWrapperProps extends BaseProps {
  readonly children: (terms: ReactNode) => ReactNode;
  readonly hideEmpty: boolean;
}

/**
 * Allows conditional rendering of an element based on if a given Term exists.
 * @param name the name of the Term / Policy to find
 * @param children a function that takes an argument of <TermsLoader> to be embedded and returns ReactNode
 * @param hideEmpty whether or not the child element should be rendered if the Term is empty
 * @returns ReactNode
 */
export const TermsLoaderWrapper: FC<TermsLoaderWrapperProps> = ({ children, name, hideEmpty }) => {
  const fetch = useFetcher(async () => await api.retrieveTermsOfAttendance(name), [name]);

  if (hideEmpty && (!fetch.data?.text || fetch.data.text.trim().length === 0)) {
    return null;
  }

  if (!fetch.data) {
    return <Fetcher result={fetch} />;
  }

  return children(<TermsLoader name={name} value={fetch.data} />);
};
